import { useNavigation } from '@react-navigation/native'
import React, { useCallback, useState } from 'react'

import { ActionIcon } from 'partials/PostCard/subcomponents'

import { Avatar } from 'widgets/Avatar'
import { Box } from 'widgets/Box'
import { BorderButton } from 'widgets/Button/BorderButton'
import { FilledButton } from 'widgets/Button/FilledButton'
import { LoadingSpinner } from 'widgets/LoadingSpinner'
import { Pressable } from 'widgets/Pressable'
import { PressableText } from 'widgets/PressableText'
import { Text } from 'widgets/Text'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useDMRoom } from 'hooks/useDMRoom'
import { useFollow } from 'hooks/useFollow'

import { links } from 'utils/constants'
import { getProfileLink } from 'utils/links/links'
import { handleShareAction } from 'utils/sharing'
import { createPartialUser } from 'utils/user/createPartialUser'

import { PartialUser } from 'types/user'

type FollowersFriendsRowProps = {
  user: PartialUser
  isCurrentUser: boolean
  isFriend?: boolean
  type: 'friends' | 'followers' | 'following'
}

export const FollowersFriendsRow = ({
  user,
  isCurrentUser,
  isFriend,
  type,
}: FollowersFriendsRowProps) => {
  const navigation = useNavigation<any>()
  const { createRoom } = useDMRoom({})

  const [isProcessingFollow, setIsProcessingFollow] = useState<boolean>(false)

  const {
    follow,
    unfollow,
    data: following,
    isLoading: isLoadingFollow,
  } = useFollow({
    trainer: user,
  })

  const { data: currentUser } = useCurrentUser()

  const navigateToProfile = () => {
    navigation.navigate(links.MEMBER_PROFILE_PAGE, {
      username: user.username,
    })
  }

  const navigateToChatRoom = useCallback(async () => {
    if (currentUser && user) {
      const { id: roomId } = await createRoom.mutateAsync({
        participants: [createPartialUser(currentUser), createPartialUser(user)],
      })

      navigation.navigate(links.CHAT_ROOM, { roomId })
    }
  }, [currentUser, user, navigation, createRoom])

  const handleFollow = async () => {
    setIsProcessingFollow(true)
    await follow.mutateAsync()
    setIsProcessingFollow(false)
  }

  const handleUnfollow = async () => {
    setIsProcessingFollow(true)
    await unfollow.mutateAsync({ followId: following?.id })
    setIsProcessingFollow(false)
  }

  const handleShareProfile = async (): Promise<void> => {
    const url = getProfileLink(user?.username as string)
    const message = `Check out ${user?.firstName} ${user?.lastName}'s Group profile!`
    await handleShareAction(url, message, navigation, links.SHARE_PROFILE_PAGE)
  }

  let buttonText = ''
  let buttonActionHandler
  switch (type) {
    case 'friends': {
      buttonText = 'Message'
      buttonActionHandler = navigateToChatRoom
      break
    }
    case 'followers': {
      buttonText = 'Follow back'
      buttonActionHandler = handleFollow
      break
    }
    case 'following': {
      buttonText = 'Following'
      buttonActionHandler = handleUnfollow
      break
    }
    default:
      break
  }

  if (user?.id === currentUser?.id) return <></>

  return (
    <Box flexDirection='row' align='center' padding={{ horizontal: 'md', vertical: 'xs' }}>
      <Pressable onPress={navigateToProfile}>
        <Avatar image={user?.avatar} width={60} height={60} />
      </Pressable>
      <Box
        padding={{ horizontal: 'sm', vertical: 'xxs' }}
        flex={1}
        align='flex-start'
        justifyContent='center'
      >
        <PressableText
          fontSize='sm'
          color='black'
          bold
          numberOfLines={2}
          onPress={navigateToProfile}
        >
          {user?.firstName} {user?.lastName}
        </PressableText>
        <PressableText fontSize='sm' color='greyDark' numberOfLines={1} onPress={navigateToProfile}>
          @{user?.username}
        </PressableText>
      </Box>
      {isCurrentUser && (
        <Box flexDirection='row' align='center' justifyContent='center'>
          {type === 'following' && (
            <ActionIcon
              direction='row'
              color='greyDark'
              icon='share'
              label=''
              action={handleShareProfile}
            />
          )}
          <Box padding={{ horizontal: 'sm' }}>
            {type === 'followers' && (
              <>
                {isFriend ? (
                  <Box padding={{ right: 'sm' }}>
                    <Text fontSize='sm' color='greyDark'>
                      Friends
                    </Text>
                  </Box>
                ) : (
                  <>
                    {isProcessingFollow ? (
                      <LoadingSpinner size='small' />
                    ) : (
                      <FilledButton
                        loading={isLoadingFollow}
                        title={buttonText}
                        fontSize='xs'
                        borderRadius='xs'
                        height={28}
                        onPress={buttonActionHandler}
                      />
                    )}
                  </>
                )}
              </>
            )}
            {(type === 'friends' || type === 'following') && (
              <>
                {isProcessingFollow && type === 'following' ? (
                  <LoadingSpinner size='small' />
                ) : (
                  <BorderButton
                    loading={type === 'following' && isLoadingFollow}
                    title={buttonText}
                    titleColor='greyDark'
                    fontSize='xs'
                    border={{ width: 1, color: 'greyDark' }}
                    borderRadius='xs'
                    height={28}
                    onPress={buttonActionHandler}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
